import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { AppConfig } from '../../app/app.config';
import { ServiceDeskTicketRequest, SupportContextKey } from '../../models/support/support.models';

@Injectable()
export class ServiceDeskTicketService {
  config = AppConfig.settings;

  constructor(private http: HttpClient) {
  }

  submitTicket(request: ServiceDeskTicketRequest): Observable<boolean> {
    const formData = new FormData();

    formData.append('subject', this.buildSubject(request));
    formData.append('name', request.name || '');
    formData.append('email', request.email || '');
    formData.append('ccName', request.ccName || '');
    formData.append('ccEmail', request.ccEmail || '');
    formData.append('reviewedTrainingVideos', `${request.reviewedTrainingVideos === true}`);
    formData.append('issueDescription', request.issueDescription || '');
    formData.append('contextLabel', request.contextLabel || '');
    formData.append('contextKey', request.contextKey || '');
    formData.append('contextDetails', JSON.stringify(request.contextDetails || {}));
    formData.append('emailBodyHtml', this.buildEmailBody(request));

    (request.attachments || []).forEach(file => {
      formData.append('attachments', file, file.name);
    });

    const url = `${this.config.apiUrl}api/support/service-desk-ticket`;

    return this.http.post<boolean>(url, formData);
  }

  private buildEmailBody(request: ServiceDeskTicketRequest): string {
    return [
      this.heading('Name', request.name),
      this.heading('Reviewed training videos', request.reviewedTrainingVideos ? 'Yes' : 'No'),
      this.heading('Current page', request.contextLabel),
      this.contextDetailsHeading(request.contextDetails),
      this.richHeading('Brief description of issue', request.issueDescription)
    ].filter(section => !!section).join('<br/><br/>');
  }

  private buildSubject(request: ServiceDeskTicketRequest): string {
    if (request.contextKey === SupportContextKey.Login) {
      return 'VESSEL APP - Issue logging in';
    }

    const contextLabel = request.contextLabel && request.contextLabel.trim();

    return contextLabel && contextLabel !== 'General'
      ? `VESSEL APP - ${contextLabel} issue`
      : 'VESSEL APP - Support request';
  }

  private heading(label: string, value: string): string {
    return `<strong>${this.escape(label)}</strong><br/>${this.escape(value || '')}`;
  }

  private richHeading(label: string, value: string): string {
    return `<strong>${this.escape(label)}</strong><br/>${this.sanitizeRichText(value || '')}`;
  }

  private contextDetailsHeading(details?: {[key: string]: string}): string {
    const contextDetails = details || {};
    const keys = Object.keys(contextDetails).filter(key => contextDetails[key]);

    if (keys.length === 0) {
      return '';
    }

    const value = keys
      .map(key => `${this.formatContextDetailLabel(key)}: ${contextDetails[key]}`)
      .join('\n');

    return this.heading('Context details', value);
  }

  private formatContextDetailLabel(key: string): string {
    switch (key) {
      case 'currentTab':
        return 'Current tab';
      case 'dailyLogDate':
        return 'Daily log date';
      case 'instanceId':
        return 'Instance ID';
      case 'operationName':
        return 'Operation name';
      case 'starting':
        return 'Starting';
      case 'type':
        return 'Type';
      case 'view':
        return 'View';
      default:
        return key;
    }
  }

  private escape(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\r?\n/g, '<br/>');
  }

  private sanitizeRichText(value: string): string {
    const container = document.createElement('div');
    container.innerHTML = value;

    Array.from(container.querySelectorAll('script, style, iframe, object, embed')).forEach(element => {
      element.parentNode.removeChild(element);
    });

    Array.from(container.querySelectorAll('*')).forEach(element => {
      Array.from(element.attributes).forEach(attribute => {
        const name = attribute.name.toLowerCase();
        const attributeValue = attribute.value || '';

        if (name.indexOf('on') === 0 || attributeValue.toLowerCase().indexOf('javascript:') === 0) {
          element.removeAttribute(attribute.name);
        }
      });
    });

    return container.innerHTML;
  }
}



// WEBPACK FOOTER //
// ./src/services/support/service-desk-ticket.service.ts